// src/components/Agent/AgentChat.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../../store/useStore';
import { agentApi } from '../../services/api';
import { X, Send } from 'lucide-react';

export default function AgentChat({ onClose }) {
  const { chatSessionId } = useStore();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I\'m ObserveAI — monitoring GCP, Azure, MongoDB, and your databases. Ask me anything about your infrastructure.' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function send() {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setLoading(true);

    // Optimistic thinking indicator
    setMessages(m => [...m, { role: 'assistant', content: '__thinking__' }]);

    try {
      const result = await agentApi.chat({ sessionId: chatSessionId, message: msg });
      setMessages(m => [...m.slice(0, -1), { role: 'assistant', content: result.response }]);
    } catch (e) {
      setMessages(m => [...m.slice(0, -1), { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  }

  const QUICK = [
    'What caused the latest error spike?',
    'Any MongoDB slow queries?',
    'Check Azure alerts',
    'Run a full health scan',
  ];

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col rounded-2xl overflow-hidden"
      style={{
        width: 360, maxHeight: 520,
        background: 'var(--bg-secondary)',
        border: '1px solid rgba(139,92,246,.4)',
        boxShadow: '0 20px 60px rgba(0,0,0,.7)',
      }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 shrink-0"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm"
          style={{ background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)' }}>🤖</div>
        <div>
          <div className="text-xs font-semibold">ObserveAI Agent</div>
          <div className="text-xs" style={{ color: '#a78bfa' }}>Ready to investigate</div>
        </div>
        <button onClick={onClose}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}>
          <X size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2" style={{ minHeight: 200 }}>
        {messages.map((m, i) => (
          <div key={i}
            className={`max-w-[88%] text-xs rounded-xl px-3 py-2 leading-relaxed ${m.role === 'user' ? 'self-end' : 'self-start'}`}
            style={m.role === 'user'
              ? { background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.25)' }
              : { background: 'rgba(139,92,246,.12)', color: 'var(--text-2)', border: '1px solid rgba(139,92,246,.2)' }
            }>
            {m.content === '__thinking__'
              ? <div className="flex gap-1"><span className="thinking-dot"/><span className="thinking-dot"/><span className="thinking-dot"/></div>
              : m.content
            }
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Quick prompts */}
      <div className="px-3 pb-2 flex gap-1 flex-wrap">
        {QUICK.map(q => (
          <button key={q} onClick={() => { setInput(q); }}
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(139,92,246,.1)', color: '#a78bfa', border: '1px solid rgba(139,92,246,.2)', cursor: 'pointer' }}>
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="flex gap-2 px-3 pb-3 pt-1">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          placeholder="Ask the agent…"
          className="flex-1 rounded-lg px-3 py-2 text-xs outline-none"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-2)', color: 'var(--text)' }}
        />
        <button onClick={send} disabled={loading || !input.trim()}
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: 'rgba(139,92,246,.25)', border: '1px solid rgba(139,92,246,.4)',
            color: '#a78bfa', cursor: loading ? 'not-allowed' : 'pointer',
          }}>
          <Send size={13} />
        </button>
      </div>
    </div>
  );
}
